import { Controller } from "@hotwired/stimulus"

// Compact searchable multi-select. Wraps a click-to-open panel containing
// a text input + checkbox list; the form submits the checked items as
// normal `name="…[]"` params, so the controller has no JSON/AJAX side
// — it's purely a UX wrapper over a native checkbox group.
//
// Expected markup (see admin/availability_coverages/show.html.erb):
//
//   <div data-controller="multi-select-search">
//     <button type="button" data-action="multi-select-search#toggle">
//       <span data-multi-select-search-target="label">All</span>
//     </button>
//     <div data-multi-select-search-target="panel">
//       <input type="text"
//              data-multi-select-search-target="search"
//              data-action="input->multi-select-search#filter">
//       <ul>
//         <li data-multi-select-search-target="row" data-label="alice smith">
//           <label>
//             <input type="checkbox" name="coach_ids[]" value="…"
//                    data-multi-select-search-target="item"
//                    data-action="change->multi-select-search#update">
//             Alice Smith
//           </label>
//         </li>
//         …
//       </ul>
//     </div>
//   </div>
//
// `data-label` on each row is what we filter against; lowercase up front
// so the keystroke handler can do a plain `includes()`.
export default class extends Controller<HTMLElement> {
  static targets = [ "panel", "search", "item", "row", "label", "noun" ]

  declare readonly panelTarget:   HTMLElement
  declare readonly searchTarget:  HTMLInputElement
  declare readonly itemTargets:   HTMLInputElement[]
  declare readonly rowTargets:    HTMLElement[]
  declare readonly labelTarget:   HTMLElement
  declare readonly hasNounTarget: boolean
  declare readonly nounTarget:    HTMLElement

  connect() {
    this.updateLabel()
    document.addEventListener("click", this.closeIfOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.closeIfOutside)
  }

  toggle(event: Event) {
    event.stopPropagation()
    const isHidden = this.panelTarget.classList.toggle("hidden")
    if (!isHidden) {
      // Defer focus so the click that opened the panel doesn't get
      // swallowed by the focus handler.
      setTimeout(() => this.searchTarget.focus(), 0)
    }
  }

  filter() {
    const q = this.searchTarget.value.trim().toLowerCase()
    for (const row of this.rowTargets) {
      const label = row.dataset.label ?? ""
      row.style.display = !q || label.includes(q) ? "" : "none"
    }
  }

  update() {
    this.updateLabel()
  }

  clearAll(event: Event) {
    event.preventDefault()
    for (const item of this.itemTargets) item.checked = false
    this.updateLabel()
  }

  private updateLabel = () => {
    const count = this.itemTargets.filter(i => i.checked).length
    const noun  = this.hasNounTarget ? this.nounTarget.textContent || "item" : "item"
    if (count === 0) {
      this.labelTarget.textContent = `Any ${noun}`
    } else if (count === 1) {
      const sel = this.itemTargets.find(i => i.checked)
      const text = sel?.parentElement?.textContent?.trim()
      this.labelTarget.textContent = text || `1 ${noun}`
    } else {
      this.labelTarget.textContent = `${count} ${noun}s selected`
    }
  }

  private closeIfOutside = (event: MouseEvent) => {
    if (!this.element.contains(event.target as Node)) {
      this.panelTarget.classList.add("hidden")
    }
  }
}
